import { useId } from 'react';
import {
  P0_QUESTION_TYPES,
  questionSchema,
  quizSchema,
  validateQuiz,
  type Question,
  type Quiz,
} from '@slideforge/shared';
import { useStrings } from '../i18n/context.js';
import type { SlideForgeState } from '../state/useSlideForge.js';

export function QuizPanel({ state }: { readonly state: SlideForgeState }) {
  const t = useStrings();
  const { selectedSlideId, quizFor, setQuiz } = state;
  const quiz = quizFor(selectedSlideId);

  if (!selectedSlideId) {
    return (
      <section aria-labelledby="sf-quiz-heading" className="sf-panel">
        <h2 id="sf-quiz-heading">{t.quizHeading}</h2>
        <p>{t.noSlideSelected}</p>
      </section>
    );
  }

  const update = (next: Quiz) => void setQuiz(selectedSlideId, next);

  return (
    <section aria-labelledby="sf-quiz-heading" className="sf-panel">
      <h2 id="sf-quiz-heading">{t.quizHeading}</h2>

      {!quiz ? (
        <button
          type="button"
          className="sf-primary"
          onClick={() =>
            void setQuiz(
              selectedSlideId,
              quizSchema.parse({ id: `quiz-${selectedSlideId}`, title: '', questions: [] }),
            )
          }
        >
          {t.addQuiz}
        </button>
      ) : (
        <>
          <QuizSettings quiz={quiz} onChange={update} />
          <QuestionList quiz={quiz} onChange={update} />
          <ValidationList quiz={quiz} />
          <button type="button" className="sf-danger" onClick={() => void setQuiz(selectedSlideId, undefined)}>
            {t.removeQuiz}
          </button>
        </>
      )}
    </section>
  );
}

function QuizSettings({ quiz, onChange }: { readonly quiz: Quiz; readonly onChange: (q: Quiz) => void }) {
  const t = useStrings();
  const base = useId();
  return (
    <div className="sf-field-group">
      <Field id={`${base}-title`} label={t.quizTitle}>
        <input
          id={`${base}-title`}
          type="text"
          value={quiz.title}
          onChange={(e) => onChange({ ...quiz, title: e.target.value })}
        />
      </Field>
      <Field id={`${base}-pass`} label={t.passingScore}>
        <input
          id={`${base}-pass`}
          type="number"
          min={0}
          max={100}
          value={quiz.passingScore}
          onChange={(e) => onChange({ ...quiz, passingScore: clamp(Number(e.target.value), 0, 100) })}
        />
      </Field>
      <Field id={`${base}-attempts`} label={t.attemptsAllowed}>
        <input
          id={`${base}-attempts`}
          type="number"
          min={1}
          value={quiz.attemptsAllowed}
          onChange={(e) => onChange({ ...quiz, attemptsAllowed: Math.max(1, Number(e.target.value)) })}
        />
      </Field>
    </div>
  );
}

function QuestionList({ quiz, onChange }: { readonly quiz: Quiz; readonly onChange: (q: Quiz) => void }) {
  const t = useStrings();

  const replace = (index: number, question: Question) => {
    const questions = [...quiz.questions];
    questions[index] = question;
    onChange({ ...quiz, questions });
  };

  return (
    <>
      <ol className="sf-questions">
        {quiz.questions.map((question, index) => (
          <li key={question.id}>
            <QuestionEditor
              question={question}
              onChange={(next) => replace(index, next)}
              onRemove={() =>
                onChange({ ...quiz, questions: quiz.questions.filter((q) => q.id !== question.id) })
              }
            />
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={() =>
          onChange({
            ...quiz,
            questions: [
              ...quiz.questions,
              questionSchema.parse({
                id: `q${quiz.questions.length + 1}-${Date.now().toString(36)}`,
                type: 'multipleChoice',
                stem: { text: '' },
                choices: [
                  { id: 'a', text: { text: '' }, isCorrect: true },
                  { id: 'b', text: { text: '' } },
                ],
              }),
            ],
          })
        }
      >
        {t.addQuestion}
      </button>
    </>
  );
}

function QuestionEditor({
  question,
  onChange,
  onRemove,
}: {
  readonly question: Question;
  readonly onChange: (q: Question) => void;
  readonly onRemove: () => void;
}) {
  const t = useStrings();
  const base = useId();
  const needsChoices = ['multipleChoice', 'multipleResponse', 'trueFalse'].includes(question.type);

  return (
    <fieldset className="sf-question">
      <legend>{question.stem.text || t.questionStem}</legend>

      <Field id={`${base}-type`} label={t.questionType}>
        <select
          id={`${base}-type`}
          value={question.type}
          onChange={(e) => onChange(changeType(question, e.target.value as Question['type']))}
        >
          {P0_QUESTION_TYPES.map((type) => (
            <option key={type} value={type}>
              {t.questionTypeNames[type] ?? type}
            </option>
          ))}
        </select>
      </Field>

      <Field id={`${base}-stem`} label={t.questionStem}>
        <textarea
          id={`${base}-stem`}
          rows={2}
          value={question.stem.text}
          onChange={(e) => onChange({ ...question, stem: { ...question.stem, text: e.target.value } })}
        />
      </Field>

      <Field id={`${base}-points`} label={t.questionPoints}>
        <input
          id={`${base}-points`}
          type="number"
          min={0}
          value={question.points}
          onChange={(e) => onChange({ ...question, points: Math.max(0, Number(e.target.value)) })}
        />
      </Field>

      {needsChoices && <ChoiceEditor question={question} onChange={onChange} />}

      {question.type === 'fillInTheBlank' && (
        <Field id={`${base}-answers`} label={t.acceptedAnswers} help={t.acceptedAnswersHelp}>
          <input
            id={`${base}-answers`}
            type="text"
            value={question.acceptedAnswers.join('; ')}
            onChange={(e) => onChange({ ...question, acceptedAnswers: splitList(e.target.value) })}
          />
        </Field>
      )}

      {question.type === 'numeric' && (
        <>
          <Field id={`${base}-numeric`} label={t.numericAnswer}>
            <input
              id={`${base}-numeric`}
              type="text"
              inputMode="decimal"
              value={question.numericAnswer ?? ''}
              onChange={(e) => onChange({ ...question, numericAnswer: parseNumber(e.target.value) })}
            />
          </Field>
          <Field id={`${base}-tolerance`} label={t.numericTolerance}>
            <input
              id={`${base}-tolerance`}
              type="text"
              inputMode="decimal"
              value={question.numericTolerance}
              onChange={(e) =>
                onChange({ ...question, numericTolerance: parseNumber(e.target.value) ?? 0 })
              }
            />
          </Field>
        </>
      )}

      <Field id={`${base}-fb-ok`} label={t.feedbackCorrect}>
        <input
          id={`${base}-fb-ok`}
          type="text"
          value={question.feedback.correct}
          onChange={(e) =>
            onChange({ ...question, feedback: { ...question.feedback, correct: e.target.value } })
          }
        />
      </Field>
      <Field id={`${base}-fb-no`} label={t.feedbackIncorrect}>
        <input
          id={`${base}-fb-no`}
          type="text"
          value={question.feedback.incorrect}
          onChange={(e) =>
            onChange({ ...question, feedback: { ...question.feedback, incorrect: e.target.value } })
          }
        />
      </Field>
      <Field id={`${base}-tags`} label={t.tags} help={t.tagsHelp}>
        <input
          id={`${base}-tags`}
          type="text"
          value={question.tags.join('; ')}
          onChange={(e) => onChange({ ...question, tags: splitList(e.target.value) })}
        />
      </Field>

      <button type="button" className="sf-danger" onClick={onRemove}>
        {t.removeQuestion}
      </button>
    </fieldset>
  );
}

function ChoiceEditor({
  question,
  onChange,
}: {
  readonly question: Question;
  readonly onChange: (q: Question) => void;
}) {
  const t = useStrings();
  const base = useId();
  const single = question.type !== 'multipleResponse';

  return (
    <div className="sf-choices" role="group" aria-label={t.choiceText}>
      {question.choices.map((choice, index) => {
        const textId = `${base}-${choice.id}-text`;
        const correctId = `${base}-${choice.id}-correct`;
        return (
          <div key={choice.id} className="sf-choice-row">
            <label htmlFor={textId} className="sf-visually-hidden">{`${t.choiceText} ${index + 1}`}</label>
            <input
              id={textId}
              type="text"
              value={choice.text.text}
              onChange={(e) => {
                const choices = [...question.choices];
                choices[index] = { ...choice, text: { ...choice.text, text: e.target.value } };
                onChange({ ...question, choices });
              }}
            />
            <input
              id={correctId}
              type={single ? 'radio' : 'checkbox'}
              name={single ? `${base}-correct` : undefined}
              checked={choice.isCorrect}
              onChange={(e) => {
                const choices = question.choices.map((c, i) => ({
                  ...c,
                  // A single-answer question can only ever have one correct
                  // choice; enforcing it here means the validation panel never
                  // has to report a state the editor allowed in the first place.
                  isCorrect: single ? i === index && e.target.checked : c.id === choice.id ? e.target.checked : c.isCorrect,
                }));
                onChange({ ...question, choices });
              }}
            />
            <label htmlFor={correctId}>{t.markCorrect}</label>
          </div>
        );
      })}
      {question.type !== 'trueFalse' && (
        <button
          type="button"
          onClick={() =>
            onChange({
              ...question,
              choices: [
                ...question.choices,
                {
                  id: nextChoiceId(question),
                  text: { text: '', latex: [] },
                  isCorrect: false,
                },
              ],
            })
          }
        >
          {t.addChoice}
        </button>
      )}
    </div>
  );
}

function ValidationList({ quiz }: { readonly quiz: Quiz }) {
  const t = useStrings();
  const issues = validateQuiz(quiz);
  return (
    <section aria-labelledby="sf-validation-heading" className="sf-validation">
      <h3 id="sf-validation-heading">{t.validationHeading}</h3>
      {issues.length === 0 ? (
        <p>{t.validationClean}</p>
      ) : (
        <ul>
          {issues.map((issue, i) => (
            <li key={`${issue.code}-${i}`} className={`sf-severity-${issue.severity}`}>
              <strong>{t.severityNames[issue.severity] ?? issue.severity}</strong>
              {': '}
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Field({
  id,
  label,
  help,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly help?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="sf-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {help && (
        <p className="sf-help" id={`${id}-help`}>
          {help}
        </p>
      )}
    </div>
  );
}

/**
 * Switching type keeps everything that still makes sense and supplies the
 * scaffolding the new type needs, rather than blanking the author's work.
 */
function changeType(question: Question, type: Question['type']): Question {
  if (type === question.type) return question;
  if (type === 'trueFalse') {
    return {
      ...question,
      type,
      choices: [
        { id: 'true', text: { text: 'Benar', latex: [] }, isCorrect: true },
        { id: 'false', text: { text: 'Salah', latex: [] }, isCorrect: false },
      ],
    };
  }
  if ((type === 'multipleChoice' || type === 'multipleResponse') && question.choices.length === 0) {
    return {
      ...question,
      type,
      choices: [
        { id: 'a', text: { text: '', latex: [] }, isCorrect: true },
        { id: 'b', text: { text: '', latex: [] }, isCorrect: false },
      ],
    };
  }
  return { ...question, type };
}

function nextChoiceId(question: Question): string {
  const used = new Set(question.choices.map((c) => c.id));
  for (const letter of 'abcdefghijklmnopqrstuvwxyz') {
    if (!used.has(letter)) return letter;
  }
  return `c${question.choices.length + 1}`;
}

function splitList(value: string): string[] {
  return value
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

function parseNumber(value: string): number | undefined {
  const n = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
}
